import React, { useState, useEffect, useRef } from 'react';
import { ArrowUp, ChevronRight, Menu, X, Instagram, Facebook, Youtube, Linkedin, CheckCircle, Mail, Phone as PhoneIcon, MapPin } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const FORMSUBMIT_ENDPOINT = 'https://formsubmit.co/ajax/info@privewealthmanagement.com';

const ContactUs = () => {
  const navigate = useNavigate();
  const [headerVisible, setHeaderVisible] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const lastScrollY = useRef(0);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    serviceInterest: '',
    message: '',
    smsConsent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Scroll handler — show back-to-top + smart-hide header (matches Home behavior)
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setShowBackToTop(currentScrollY > 400);
      if (currentScrollY < 100) {
        setHeaderVisible(true);
      } else if (currentScrollY > lastScrollY.current && currentScrollY > 150) {
        setHeaderVisible(false);
        setMobileMenuOpen(false);
      } else if (currentScrollY < lastScrollY.current) {
        setHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Intersection observer for fade-ins
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('visible');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -100px 0px' });
    document.querySelectorAll('.fade-in-up, .fade-in-left, .fade-in-right, .scale-in, .fade-in')
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [submitted]);

  const handleNavClick = (sectionId) => {
    setMobileMenuOpen(false);
    navigate(`/#${sectionId}`);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');

    const payload = {
      _subject: `New Contact Form Submission from ${formData.firstName} ${formData.lastName}`,
      _template: 'table',
      _captcha: 'false',
      'First Name': formData.firstName,
      'Last Name': formData.lastName,
      Phone: formData.phone,
      'Service of Interest': formData.serviceInterest || 'Not specified',
      Message: formData.message,
      'SMS Consent': formData.smsConsent ? 'YES — consents to SMS' : 'No — did not opt in to SMS',
      'Submitted From': 'https://www.privewealthmanagement.com/contact-us',
    };

    try {
      const res = await fetch(FORMSUBMIT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data && (data.success === 'true' || data.success === true)) {
        setSubmitted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setSubmitError("We couldn't send your message right now. Please try again or call us at (470) 737-4131.");
      }
    } catch (err) {
      setSubmitError("We couldn't send your message right now. Please try again or call us at (470) 737-4131.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="luxury-landing">
      {/* Header — visually identical to Home */}
      <header className={`navigation-header ${headerVisible ? 'header-visible' : 'header-hidden'}`} data-testid="contact-header">
        <div className="container">
          <div className="nav-content">
            <Link to="/" className="navigation-logo" data-testid="contact-logo-link">
              <img
                src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/0u8dfr1l_Prive%CC%81%20WM%20Main%20Logo%202.png"
                alt="Privé Wealth Management"
                className="header-logo"
              />
            </Link>

            <nav className="desktop-nav">
              <ul className="navigation-menu">
                <li><button onClick={() => handleNavClick('how-we-help')} className="navigation-link" data-testid="nav-how-we-help">How We Help</button></li>
                <li><button onClick={() => handleNavClick('process')} className="navigation-link" data-testid="nav-process">Our Process</button></li>
                <li><button onClick={() => handleNavClick('services')} className="navigation-link" data-testid="nav-services">Services</button></li>
                <li><button onClick={() => handleNavClick('pricing')} className="navigation-link" data-testid="nav-pricing">Pricing</button></li>
                <li><button onClick={() => handleNavClick('heritage')} className="navigation-link" data-testid="nav-heritage">Privé Heritage</button></li>
                <li><Link to="/contact-us" className="navigation-link" data-testid="nav-contact">Contact</Link></li>
              </ul>
            </nav>

            <button
              className="mobile-menu-toggle"
              onClick={() => setMobileMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              data-testid="contact-mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        <div className={`mobile-nav ${mobileMenuOpen ? 'mobile-nav-open' : ''}`}>
          <nav>
            <ul className="mobile-menu-list">
              <li><button onClick={() => handleNavClick('how-we-help')} className="mobile-nav-link">How We Help</button></li>
              <li><button onClick={() => handleNavClick('process')} className="mobile-nav-link">Our Process</button></li>
              <li><button onClick={() => handleNavClick('services')} className="mobile-nav-link">Services</button></li>
              <li><button onClick={() => handleNavClick('pricing')} className="mobile-nav-link">Pricing</button></li>
              <li><button onClick={() => handleNavClick('heritage')} className="mobile-nav-link">Privé Heritage</button></li>
              <li><Link to="/contact-us" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Contact</Link></li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Page hero */}
      <section style={{ background: '#fffef2', padding: '140px 0 60px', textAlign: 'center' }} data-testid="contact-hero">
        <div className="container">
          <div className="fade-in-up" style={{ maxWidth: '800px', margin: '0 auto' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '16px' }}>
              Get in Touch
            </p>
            <h1 style={{ fontSize: 'clamp(40px, 5vw, 56px)', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '20px', lineHeight: '1.1' }}>
              Let's Take the First Step Together
            </h1>
            <p style={{ fontSize: '18px', color: '#555', lineHeight: '1.7', maxWidth: '640px', margin: '0 auto' }}>
              No obligation, no pressure — just a conversation about your financial future. Tell us a bit about yourself and we'll be in touch within one business day.
            </p>
          </div>
        </div>
      </section>

      {/* Form + side info */}
      <section style={{ background: '#fffef2', padding: '20px 0 120px' }}>
        <div className="container">
          <div className="contact-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '60px', maxWidth: '1100px', margin: '0 auto', alignItems: 'start' }}>
            {/* FORM CARD */}
            <div className="fade-in-up" style={{ background: '#f6f5e8', border: '2px solid #C9A961', padding: '48px' }}>
              {submitted ? (
                <div data-testid="contact-thank-you" style={{ textAlign: 'center', padding: '40px 16px' }}>
                  <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '72px', height: '72px', borderRadius: '50%', background: 'rgba(201, 169, 97, 0.18)', border: '2px solid #C9A961', marginBottom: '24px', color: '#C9A961' }}>
                    <CheckCircle size={36} strokeWidth={1.5} />
                  </div>
                  <h2 style={{ fontSize: '32px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '16px' }}>Thank you — your message is on its way.</h2>
                  <p style={{ fontSize: '16px', color: '#555', lineHeight: '1.7', maxWidth: '440px', margin: '0 auto 32px' }}>
                    We've received your contact information and a member of our team will reach out within one business day. If your matter is time-sensitive, you can reach us directly at (470) 737-4131.
                  </p>
                  <Link to="/" className="btn-primary" data-testid="contact-return-home" style={{ display: 'inline-flex' }}>
                    Return Home
                    <ChevronRight size={16} />
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form" noValidate>
                  <h2 style={{ fontSize: '24px', fontFamily: "'Crimson Text', serif", color: '#232e19', marginBottom: '8px' }}>Send us a message</h2>
                  <p style={{ fontSize: '14px', color: '#666', marginBottom: '28px' }}>Fields marked with an asterisk are required.</p>

                  {/* Name row */}
                  <div className="form-row-two" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '18px' }}>
                    <div>
                      <label htmlFor="firstName" style={labelStyle}>First Name *</label>
                      <input
                        id="firstName" name="firstName" type="text" required
                        value={formData.firstName} onChange={handleChange}
                        style={inputStyle} data-testid="contact-first-name"
                        autoComplete="given-name"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" style={labelStyle}>Last Name *</label>
                      <input
                        id="lastName" name="lastName" type="text" required
                        value={formData.lastName} onChange={handleChange}
                        style={inputStyle} data-testid="contact-last-name"
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  {/* Phone */}
                  <div style={{ marginBottom: '18px' }}>
                    <label htmlFor="phone" style={labelStyle}>Phone *</label>
                    <input
                      id="phone" name="phone" type="tel" required
                      value={formData.phone} onChange={handleChange}
                      style={inputStyle} data-testid="contact-phone"
                      autoComplete="tel"
                      placeholder="(470) 555-1234"
                    />
                  </div>

                  {/* Service of interest */}
                  <div style={{ marginBottom: '18px' }}>
                    <label htmlFor="serviceInterest" style={labelStyle}>Service of Interest</label>
                    <select
                      id="serviceInterest" name="serviceInterest"
                      value={formData.serviceInterest} onChange={handleChange}
                      style={{ ...inputStyle, appearance: 'none', backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23232e19' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>\")", backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: '44px', cursor: 'pointer' }}
                      data-testid="contact-service-interest"
                    >
                      <option value="">Select an option (optional)</option>
                      <option value="Financial Organization">Financial Organization</option>
                      <option value="Insurance Planning">Insurance Planning</option>
                      <option value="Financial Planning">Financial Planning</option>
                      <option value="Ongoing Advisory Retainer">Ongoing Advisory Retainer</option>
                      <option value="Investment Management">Investment Management</option>
                      <option value="Privé Heritage (Multi-Family Office)">Privé Heritage (Multi-Family Office)</option>
                      <option value="Just exploring">Just exploring</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div style={{ marginBottom: '20px' }}>
                    <label htmlFor="message" style={labelStyle}>Message *</label>
                    <textarea
                      id="message" name="message" rows="5" required
                      value={formData.message} onChange={handleChange}
                      style={{ ...inputStyle, minHeight: '140px', resize: 'vertical', fontFamily: 'inherit' }}
                      data-testid="contact-message"
                      placeholder="Tell us a bit about your situation and what you're hoping to accomplish."
                    />
                  </div>

                  {/* SMS Consent block */}
                  <div style={{ background: '#fffef2', border: '1px solid rgba(201, 169, 97, 0.5)', padding: '20px', marginBottom: '24px' }} data-testid="sms-consent-block">
                    <label htmlFor="smsConsent" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer' }}>
                      <input
                        id="smsConsent" name="smsConsent" type="checkbox"
                        checked={formData.smsConsent} onChange={handleChange}
                        style={{ marginTop: '4px', width: '18px', height: '18px', accentColor: '#C9A961', flexShrink: 0, cursor: 'pointer' }}
                        data-testid="sms-consent-checkbox"
                      />
                      <span style={{ fontSize: '14px', color: '#232e19', lineHeight: '1.5' }}>
                        <span style={{ color: '#888', fontWeight: 'normal' }}>(Optional)</span> <strong>I consent to receive SMS text messages from Privé Wealth Management.</strong>
                      </span>
                    </label>
                    <p style={{ fontSize: '12px', color: '#555', lineHeight: '1.7', marginTop: '12px', paddingLeft: '30px' }}>
                      By submitting this form with this box checked, you consent to receive informational messages, appointment reminders, and support messages from Privé Wealth Management SMS at the number provided. Msg &amp; data rates may apply. Msg frequency varies. Reply HELP for help or STOP to unsubscribe at any time. We do not share your phone number with third parties.{' '}
                      <a href="/privacy" style={consentLinkStyle} target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                      {' '}&amp;{' '}
                      <a href="/terms" style={consentLinkStyle} target="_blank" rel="noopener noreferrer">Terms</a>
                      .
                    </p>
                  </div>

                  {submitError && (
                    <div data-testid="contact-form-error" style={{ background: 'rgba(180, 50, 50, 0.08)', border: '1px solid rgba(180, 50, 50, 0.5)', padding: '14px 18px', marginBottom: '20px', color: '#8b2a2a', fontSize: '14px', lineHeight: '1.5' }}>
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting}
                    data-testid="contact-submit-button"
                    style={{ width: '100%', justifyContent: 'center', opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
                  >
                    {submitting ? 'Sending…' : 'Send Message'}
                    {!submitting && <ChevronRight size={16} />}
                  </button>
                </form>
              )}
            </div>

            {/* SIDE INFO */}
            <div className="fade-in-up">
              <div style={{ background: 'linear-gradient(135deg, #232e19 0%, #1a2412 100%)', color: '#fffef2', padding: '36px 32px', marginBottom: '24px' }}>
                <p style={{ fontSize: '12px', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600', marginBottom: '14px' }}>What to Expect</p>
                <h3 style={{ fontSize: '22px', fontFamily: "'Crimson Text', serif", marginBottom: '16px', color: '#fffef2' }}>A real conversation. No pitch.</h3>
                <p style={{ fontSize: '15px', lineHeight: '1.7', color: 'rgba(255,254,242,0.85)' }}>
                  Within one business day, a Privé advisor will reach out to learn more about your goals and explain how we can help. You'll never be pressured to commit, sign, or buy anything on the call.
                </p>
              </div>

              <div style={{ background: '#f6f5e8', border: '1px solid #C9A961', padding: '28px' }}>
                <p style={{ fontSize: '12px', color: '#C9A961', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: '600', marginBottom: '20px' }}>Direct Contact</p>
                <div style={{ display: 'flex', gap: '14px', marginBottom: '16px', alignItems: 'flex-start' }}>
                  <PhoneIcon size={18} style={{ color: '#C9A961', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>Phone</div>
                    <a href="tel:+14707374131" style={{ fontSize: '15px', color: '#232e19', fontWeight: '600', textDecoration: 'none' }}>(470) 737-4131</a>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', marginBottom: '16px', alignItems: 'flex-start' }}>
                  <Mail size={18} style={{ color: '#C9A961', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>Email</div>
                    <a href="mailto:info@privewealthmanagement.com" style={{ fontSize: '14px', color: '#232e19', fontWeight: '600', textDecoration: 'none', wordBreak: 'break-all' }}>info@privewealthmanagement.com</a>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <MapPin size={18} style={{ color: '#C9A961', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '2px' }}>Office</div>
                    <div style={{ fontSize: '14px', color: '#232e19', lineHeight: '1.5' }}>Grayson, Georgia</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer — identical to Home */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo fade-in">
              <img
                src="https://customer-assets.emergentagent.com/job_3d73a7a2-fafd-4cdc-8e3f-68ea2705c157/artifacts/0u8dfr1l_Prive%CC%81%20WM%20Main%20Logo%202.png"
                alt="Privé Wealth Management"
                className="footer-logo-img"
              />
            </div>

            <div className="footer-social fade-in">
              <a href="https://www.instagram.com/officialprivewm/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram"><Instagram size={20} /></a>
              <a href="https://www.facebook.com/privewm" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook"><Facebook size={20} /></a>
              <a href="https://www.youtube.com/@OfficialPriveWM" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube"><Youtube size={20} /></a>
              <a href="https://www.tiktok.com/@officialprivewm" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="TikTok">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/officialprivewm" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn"><Linkedin size={20} /></a>
              <a href="https://x.com/OfficialPriveWM" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="X (Twitter)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
            </div>

            <div className="footer-links fade-in-up">
              <a href="https://customer-assets.emergentagent.com/job_215f2b22-f0a3-4699-9cdc-907a59cf6351/artifacts/0it15obz_Prive-Form-CRS.pdf" className="footer-link" target="_blank" rel="noopener noreferrer">Form CRS</a>
              <a href="https://files.adviserinfo.sec.gov/IAPD/Content/Common/crd_iapd_Brochure.aspx?BRCHR_VRSN_ID=1043573" className="footer-link" target="_blank" rel="noopener noreferrer">ADV Firm Brochure</a>
              <a href="https://adviserinfo.sec.gov" className="footer-link" target="_blank" rel="noopener noreferrer">SEC Investment Adviser Search</a>
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </div>
            <div className="footer-disclaimer fade-in-up">
              <p className="body-small">
                Privé Wealth Management, LLC is a Georgia-registered investment advisory firm. Registration does not imply a certain level of skill or training. All investment advisory services are provided in a fiduciary capacity and are tailored to the individual needs and objectives of each client. Advisory services are only offered to clients or prospective clients where Privé Wealth Management, LLC and its representatives are properly registered or exempt from registration. Past performance is not indicative of future results. No guarantees are made regarding investment outcomes.
              </p>
            </div>
            <div className="footer-bottom">
              <p className="body-small">© 2026 Privé Wealth Management, LLC. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>

      {showBackToTop && (
        <button className="back-to-top" onClick={scrollToTop} aria-label="Back to top" data-testid="contact-back-to-top">
          <ArrowUp size={20} />
        </button>
      )}
    </div>
  );
};

const labelStyle = {
  display: 'block',
  fontSize: '13px',
  fontWeight: '600',
  color: '#232e19',
  marginBottom: '6px',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
};

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  fontSize: '15px',
  color: '#232e19',
  background: '#fffef2',
  border: '1px solid rgba(201, 169, 97, 0.5)',
  borderRadius: '0',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease',
};

const consentLinkStyle = {
  color: '#C9A961',
  textDecoration: 'underline',
  wordBreak: 'break-word',
};

export default ContactUs;
